import { FLAG_SPACES } from '@app/feature-flags/flags';
import { useOrganizationExistsQuery } from '@app/graphql';
import {
  isFirstSpaceLoading,
  selectCurrentSpaceExternalId,
  selectFirstSpaceData,
  selectInitialSpaceIdResolved,
} from '@app/routes/organization/selectors';
import { useAppDispatch, useAppSelector } from '@app/store';
import { selectFlags } from '@app/store/selectors';
import { useEffect } from 'react';

import { getLastVisitedSpace } from './storage-spaces-cache';

/**
 * Resolves the active Space and dispatches the appropriate Redux event.
 *
 * Precedence: URL `space_external_id` param → last-visited (localStorage) →
 * first org space → empty (flag off or no spaces).
 *
 * Pass `skipWhenResolved: false` from callers that also need
 * `data.organization` for an org-existence guard (e.g. OrganizationWrapper).
 */
export function useInitialSpaceResolution({
  organizationId,
  userId,
  candidateSpaceExternalId,
  skipWhenResolved = true,
}: {
  organizationId: string;
  userId: string;
  candidateSpaceExternalId?: string;
  skipWhenResolved?: boolean;
}) {
  const isInitialSpaceIdResolved = useAppSelector(selectInitialSpaceIdResolved);
  const currentSpaceExternalId = useAppSelector(selectCurrentSpaceExternalId);
  const firstSpaceData = useAppSelector(selectFirstSpaceData);
  const isFirstSpaceDataLoading = useAppSelector(isFirstSpaceLoading);
  const flags = useAppSelector(selectFlags);
  const isSpacesFlagEnabled = Boolean(flags?.[FLAG_SPACES]);
  const dispatch = useAppDispatch();

  const skip =
    skipWhenResolved && !candidateSpaceExternalId && isInitialSpaceIdResolved;

  const lastVisitedSpaceExternalId = getLastVisitedSpace(
    userId,
    organizationId,
  );
  const spaceToValidate =
    candidateSpaceExternalId || lastVisitedSpaceExternalId || undefined;

  const [{ data, error, fetching }] = useOrganizationExistsQuery({
    variables: {
      organizationId,
      spaceExternalIds: spaceToValidate ? [spaceToValidate] : undefined,
    },
    pause: !organizationId || !userId || skip,
  });

  useEffect(() => {
    if (fetching || !organizationId || !userId) return;
    // Wait for feature flags — dispatching before they load can produce an
    // unscoped auth request when FLAG_SPACES is still undefined.
    if (flags === undefined) return;
    if (isInitialSpaceIdResolved && !candidateSpaceExternalId) return;

    // Flag off → always dispatch empty selection (legacy org-wide behavior).
    const resolvedSpaceExternalId =
      !error && isSpacesFlagEnabled
        ? data?.organization?.spaces?.edges[0]?.node?.externalId ||
          firstSpaceData?.externalId ||
          ''
        : '';
    const resolvedSpaceName =
      !error && isSpacesFlagEnabled
        ? data?.organization?.spaces?.edges[0]?.node?.name ||
          firstSpaceData?.name ||
          ''
        : '';

    const hasDifferentCandidate =
      isSpacesFlagEnabled &&
      candidateSpaceExternalId &&
      isInitialSpaceIdResolved &&
      resolvedSpaceExternalId &&
      resolvedSpaceExternalId !== currentSpaceExternalId;

    if (hasDifferentCandidate) {
      dispatch({
        type: '[organization] org space changed',
        payload: {
          spaceExternalId: resolvedSpaceExternalId,
          spaceName: resolvedSpaceName,
        },
      });
      return;
    }

    // Fire on error too so saga waiters are never blocked; auth will fail
    // closed on an empty space when FLAG_SPACES is on.
    if (
      !isInitialSpaceIdResolved &&
      (resolvedSpaceExternalId ||
        !isSpacesFlagEnabled ||
        !isFirstSpaceDataLoading ||
        error)
    ) {
      dispatch({
        type: '[organization] initial spaceid resolved',
        payload: {
          spaceExternalId: resolvedSpaceExternalId,
          spaceName: resolvedSpaceName,
          resolved: true,
        },
      });
    }
  }, [
    data,
    error,
    fetching,
    flags,
    organizationId,
    userId,
    isInitialSpaceIdResolved,
    currentSpaceExternalId,
    candidateSpaceExternalId,
    dispatch,
    firstSpaceData?.externalId,
    firstSpaceData?.name,
    isFirstSpaceDataLoading,
    isSpacesFlagEnabled,
  ]);

  return { data, error, fetching };
}
